/**
 * useSavePuzzleRoyaleResult
 *
 * POST /fun_challenges/v1/puzzle_royale_results/
 *
 * Idempotent on `session_id` (history row) + `(history_id, user_id)` (per-
 * player rows) server-side via update_or_create. Any client may fire this on
 * `fun_session_end`; later calls no-op.
 *
 * N-player payload: per-participant data lives in `players[]` (2..100). The
 * server auth-guards on "requesting user ∈ players[].user_id" (NOT the 2-player
 * player1/player2 ownership the other modes use). Bearer auto-injected by
 * ChessRestClient.
 */

import {useMutation} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';
import type {PuzzleAttempt} from '@/Screens/FunChallenges/util/attempts';
import type {PieceTier, RoyaleEndReason} from '@/Screens/FunChallenges/royaleTypes';

export interface PuzzleRoyalePlayerResultPayload {
  user_id: number;
  points: number;          // solves + streak bonuses
  score: number;           // solved count
  wrong: number;
  best_combo: number;
  piece: PieceTier;        // tier at finish
  rank: number;            // 1-based final placement
  accuracy: number;        // 0.0..1.0
  solved_ids: number[];
  failed_ids: number[];
  attempts: PuzzleAttempt[];
  eliminated: boolean;     // punish mistake-out
  finished_at?: string | null;
}

export interface SavePuzzleRoyaleResultPayload {
  session_id: string;
  game_type: string;                  // 'puzzle_royale'
  /**
   * SOLO Puzzle Royale (1-player). When true the server accepts a single-player
   * players[] (the >=2 guard is relaxed on this flag). Absent/false = normal
   * multiplayer royale (>= 2 required).
   */
  solo?: boolean;
  reason: RoyaleEndReason;
  puzzle_ids: number[];
  time_control_sec: number;
  topics: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  punish: boolean;
  punish_threshold?: number | null;
  players: PuzzleRoyalePlayerResultPayload[];
  end_reason: RoyaleEndReason;
}

export interface SavePuzzleRoyaleResultResponse {
  ok: boolean;
  history_id?: number;
  error?: string;
}

export default function useSavePuzzleRoyaleResult(
  onSuccess?: (data: SavePuzzleRoyaleResultResponse) => void,
  onError?: (err: unknown) => void,
) {
  return useMutation<
    SavePuzzleRoyaleResultResponse,
    unknown,
    SavePuzzleRoyaleResultPayload
  >(
    async (payload: SavePuzzleRoyaleResultPayload) => {
      const response = await ChessRestClient.post(
        'fun_challenges/v1/puzzle_royale_results/',
        payload,
      );
      return response.data as SavePuzzleRoyaleResultResponse;
    },
    {
      onSuccess: data => {
        if (onSuccess) onSuccess(data);
      },
      onError: err => {
        if (onError) onError(err);
      },
    },
  );
}
